"use client";

import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";

/**
 * Echtzeit-3D-Slicer & Druckkopf (60fps HTML5 Canvas):
 * Ein rotierendes 3D-Bauteil wird Schicht für Schicht (Layer by Layer)
 * von einem glühenden Laser-Extruder aufgebaut!
 */
export default function Preview() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    let angle = 0;

    const totalLayers = 7;

    const render = (time: number) => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      if (canvas.width !== rect.width * dpr || canvas.height !== rect.height * dpr) {
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
      }
      ctx.save();
      ctx.scale(dpr, dpr);
      const w = rect.width;
      const h = rect.height;
      ctx.clearRect(0, 0, w, h);

      angle += 0.014;
      // Zyklischer Schicht-Fortschritt von 1 bis totalLayers
      const progress = ((time * 0.0012) % (totalLayers + 1.5));
      const activeLayers = Math.min(totalLayers, Math.floor(progress) + 1);

      const cx = w / 2;
      const baseY = h * 0.72;

      // Zeichne 6-eckige isometrische Druck-Schichten von unten nach oben
      for (let layer = 0; layer < totalLayers; layer++) {
        const isPrinted = layer < activeLayers;
        const isCurrentTop = layer === activeLayers - 1;
        const layerY = baseY - layer * 11;
        const radiusX = 44 - layer * 2.2;
        const radiusY = radiusX * 0.42;

        ctx.beginPath();
        const sides = 6;
        for (let s = 0; s <= sides; s++) {
          const theta = angle + (s * Math.PI * 2) / sides;
          const px = cx + Math.cos(theta) * radiusX;
          const py = layerY + Math.sin(theta) * radiusY;
          if (s === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }

        if (isCurrentTop) {
          ctx.strokeStyle = "#10b981";
          ctx.fillStyle = "rgba(16, 185, 129, 0.22)";
          ctx.shadowColor = "#10b981";
          ctx.shadowBlur = 14;
          ctx.lineWidth = 2.4;
          ctx.fill();
          ctx.stroke();
          ctx.shadowBlur = 0;

          // Zeichne glühende Düse (Nozzle) + Laserstrahl von oben auf die aktuelle Ecke
          const nozzleTheta = angle + ((time * 0.006) % (Math.PI * 2));
          const targetX = cx + Math.cos(nozzleTheta) * radiusX;
          const targetY = layerY + Math.sin(nozzleTheta) * radiusY;

          // Laserstrahl von Düse zu Druckpunkt
          ctx.beginPath();
          ctx.moveTo(cx, 16);
          ctx.lineTo(targetX, targetY);
          ctx.strokeStyle = "rgba(232, 121, 249, 0.85)";
          ctx.lineWidth = 2;
          ctx.stroke();

          // Schmelzpunkt-Funke
          ctx.beginPath();
          ctx.arc(targetX, targetY, 4.5, 0, Math.PI * 2);
          ctx.fillStyle = "#fff";
          ctx.shadowColor = "#e879f9";
          ctx.shadowBlur = 14;
          ctx.fill();
          ctx.shadowBlur = 0;
        } else if (isPrinted) {
          ctx.strokeStyle = "rgba(168, 85, 247, 0.75)";
          ctx.fillStyle = "rgba(168, 85, 247, 0.12)";
          ctx.lineWidth = 1.6;
          ctx.fill();
          ctx.stroke();
        } else {
          // Noch nicht gedruckte Ghost-Wireframe-Schichten
          ctx.strokeStyle = "rgba(148, 163, 184, 0.18)";
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      }

      ctx.restore();
      animId = requestAnimationFrame(render);
    };

    animId = requestAnimationFrame(render);
    return () => cancelAnimationFrame(animId);
  }, []);

  return (
    <div
      aria-hidden="true"
      className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-2xl select-none"
    >
      <motion.div
        animate={{ scale: [1, 1.25, 1], opacity: [0.45, 0.8, 0.45] }}
        transition={{ duration: 4, repeat: Infinity }}
        className="pointer-events-none absolute h-36 w-48 rounded-full bg-purple-500/30 blur-3xl"
      />
      <canvas ref={canvasRef} className="h-full w-full" />
      <div className="pointer-events-none absolute bottom-2.5 rounded-full border border-purple-500/40 bg-white/90 px-3 py-0.5 font-mono text-[10px] font-extrabold text-purple-700 shadow-md backdrop-blur-md dark:bg-zinc-900/90 dark:text-purple-300">
        Prompt ➔ 3D G-Code ✓
      </div>
    </div>
  );
}
